import { isPlatformBrowser } from '@angular/common';
import { Component, PLATFORM_ID, inject, signal } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { environment } from '@environment/environment';

@Component({
  selector: 'app-waitlist',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './waitlist.html',
  styleUrl: './waitlist.scss',
})
export class Waitlist {
  private readonly fb = inject(FormBuilder);
  private readonly platformId = inject(PLATFORM_ID);

  submitted = false;
  readonly sending = signal(false);
  readonly sendSuccess = signal(false);
  readonly sendError = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    fullName: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: ['', [Validators.required, Validators.minLength(7)]],
  });

  get f() {
    return this.form.controls;
  }

  scrollToForm(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    document.getElementById('waitlist-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  showError(controlName: keyof typeof this.form.controls): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (control.touched || control.dirty || this.submitted);
  }

  errorMessage(controlName: keyof typeof this.form.controls): string {
    const control = this.form.controls[controlName];
    if (!control.errors) return '';

    if (control.errors['required']) {
      switch (controlName) {
        case 'fullName':
          return 'Please enter your full name.';
        case 'email':
          return 'Please enter your email address.';
        case 'phoneNumber':
          return 'Please enter your phone number.';
      }
    }
    if (control.errors['email']) {
      return 'Please enter a valid email address.';
    }
    if (control.errors['minlength']) {
      const min = control.errors['minlength'].requiredLength as number;
      if (controlName === 'fullName') {
        return `Name must be at least ${min} characters.`;
      }
      if (controlName === 'phoneNumber') {
        return `Phone number must be at least ${min} characters.`;
      }
    }
    return 'This field is invalid.';
  }

  async submitWaitlist(): Promise<void> {
    this.submitted = true;
    this.sendSuccess.set(false);
    this.sendError.set(null);
    this.form.markAllAsTouched();

    if (this.form.invalid || this.sending()) {
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const { publicKey, serviceId, templateId } = environment.emailjs;
    if (
      !publicKey ||
      !serviceId ||
      !templateId ||
      publicKey.startsWith('YOUR_') ||
      serviceId.startsWith('YOUR_') ||
      templateId.startsWith('YOUR_')
    ) {
      this.sendError.set(
        'Waitlist is not configured yet. Add your EmailJS keys in environment.ts.',
      );
      return;
    }

    const { fullName, email, phoneNumber } = this.form.getRawValue();
    this.sending.set(true);

    try {
      const emailjs = (await import('@emailjs/browser')).default;
      await emailjs.send(
        serviceId,
        templateId,
        {
          full_name: fullName,
          from_email: email,
          phone_number: `+234 ${phoneNumber}`,
          reason: 'Waitlist signup',
          message: `Waitlist signup from ${fullName} (${email}, +234 ${phoneNumber})`,
          to_email: 'info@luminaauction.com',
        },
        { publicKey },
      );

      this.sendSuccess.set(true);
      this.form.reset();
      this.submitted = false;
    } catch {
      this.sendError.set(
        'Something went wrong. Please try again or email us at support@lumina.ng.',
      );
    } finally {
      this.sending.set(false);
    }
  }
}
